// contractPctService.js
import { readContract, writeContract, waitForTransactionReceipt } from '@wagmi/core';
// import Swal from "sweetalert2";
import { abi } from './pct_abi';
import { abi as rewardsAbi } from './pct_rewards_abi';
import addresses from './addresses';
import utils from '@/composables/utils';
import { getPairReserves, getPairToken0 } from './contractPairService';
import BigNumber from 'bignumber.js';
import { Interface } from 'ethers';

const { convertToWei, convertFromWei } = utils;
const DEFAULT_DECIMALS = 18;  // 如果合约使用 18 位小数

import { config } from './walletConfig';
import { queryRecentContractEvents } from './ethService';
/**
 * 获取合约的 decimals
 * @returns {Promise<number>}
 */
export async function getDecimals() {
  try {
    const decimals = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "decimals",
      args: []
    });
    return Number(decimals);
  } catch (error) {
    console.error("Error in getDecimals:", error);
    throw error;
  }
}

/**
 * 获取总供给量，并根据 decimals 转换成常规单位
 * @returns {Promise<string>}
 */
export async function totalSupply() {
  try {
    const decimals = await getDecimals();
    const supply = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalSupply",
      args: []
    });
    return convertFromWei(supply, decimals, 2);
  } catch (error) {
    console.error("Error in totalSupply:", error);
    throw error;
  }
}

/**
 * 执行 ERC20 approve 操作
 * @param {string} spender 要授权的地址
 * @param {number|string} amount 授权数量（常规单位）
 * @param {number} decimals 小数位，默认为18
 * @returns {Promise<any>}
 */
export async function approvePct(spender, amount, decimals = DEFAULT_DECIMALS) {
  try {
    const amountWei = convertToWei(amount, decimals);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "approve",
      args: [spender, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in approvePct:", error);
    throw error;
  }
}

/**
 * 执行 ERC20 transfer 操作
 * @param {string} to 接收地址
 * @param {number|string} amount 转账数量（常规单位）
 * @param {number} decimals 小数位，默认为18
 * @returns {Promise<any>}
 */
export async function transferPct(to, amount, decimals = DEFAULT_DECIMALS) {
  try {
    const amountWei = convertToWei(amount, decimals);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "transfer",
      args: [to, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in transferPct:", error);
    throw error;
  }
}

/**
 * 监控交易状态并返回 receipt
 * @param {string} txHash 交易哈希
 * @returns {Promise<any>}
 */
export async function monitorTransaction(txHash) {
  try {
    const receipt = await waitForTransactionReceipt(config, { hash: txHash });
    return receipt;
  } catch (error) {
    console.error("Error in monitorTransaction:", error);
    throw error;
  }
}

/**
 * 执行 ERC20 transferFrom 操作
 * @param {string} from 转出地址
 * @param {string} to 接收地址
 * @param {number|string} amount 转账数量（常规单位）
 * @param {number} decimals 小数位，默认为18
 * @returns {Promise<any>}
 */
export async function transferFromPct(from, to, amount, decimals = DEFAULT_DECIMALS) {
  try {
    const amountWei = convertToWei(amount, decimals);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "transferFrom",
      args: [from, to, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in transferFromPct:", error);
    throw error;
  }
}

/**
 * 更新资产地址
 * @param {string} assetAddr 新的资产地址
 * @returns {Promise<any>}
 */
export async function updateAssetAddressPct(assetAddr) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "updateAssetAddress",
      args: [assetAddr]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in updateAssetAddressPct:", error);
    throw error;
  }
}

/**
 * 设置代币价格
 * @param {string} newPriceAddress 新的价格地址
 * @returns {Promise<any>}
 */
export async function setPriceForTokenPct(newPriceAddress) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "setPriceForToken",
      args: [newPriceAddress]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in setPriceForTokenPct:", error);
    throw error;
  }
}

/**
 * 获取合约的授权额度
 * @param {string} owner 授权者地址
 * @param {string} spender 被授权者地址
 * @returns {Promise<number>}
 */
export async function getAllowance(owner, spender) {
  try {
    const allowance = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "allowance",
      args: [owner, spender]
    });
    console.log('allowance', convertFromWei(allowance, DEFAULT_DECIMALS, 2));
    return convertFromWei(allowance, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in getAllowance:", error);
    throw error;
  }
}

/**
 * 获取合约的资产地址
 * @returns {Promise<string>}
 */
export async function getAssetAddress() {
  try {
    const assetAddress = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "assetAddress",
      args: []
    });
    return assetAddress;
  } catch (error) {
    console.error("Error in getAssetAddress:", error);
    throw error;
  }
}

/**
 * 获取合约的总资产
 * @returns {Promise<string>}
 */
export async function getTotalAssets() {
  try {
    const totalAssets = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalAssets",
      args: []
    });
    return totalAssets;
  } catch (error) {
    console.error("Error in getTotalAssets:", error);
    throw error;
  }
}

/**
 * 获取合约的资产管理者地址
 * @returns {Promise<string>}
 */
export async function getAssetManager() {
  try {
    const assetManager = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "assetManager",
      args: []
    });
    return assetManager;
  } catch (error) {
    console.error("Error in getAssetManager:", error);
    throw error;
  }
}

/**
 * 获取代币的ETH价格
 * @returns {Promise<string>}
 */
export async function getPriceETH() {
  try {
    const price = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "getPriceETH",
      args: []
    });
    return convertFromWei(price, 8, 6);
  } catch (error) {
    console.error("Error in getPriceETH:", error);
    throw error;
  }
}

/**
 * 获取推荐收入
 * @param {string} address 要查询的地址
 * @returns {Promise<{level1: string, level2: string, level3: string}>}
 */
export async function getReferralIncomes(address) {
  try {
    const [level1, level2, level3] = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "getReferralIncomes",
      args: [address]
    });
    return {
      level1: convertFromWei(level1, DEFAULT_DECIMALS, 2),
      level2: convertFromWei(level2, DEFAULT_DECIMALS, 2),
      level3: convertFromWei(level3, DEFAULT_DECIMALS, 2)
    };
  } catch (error) {
    console.error("Error in getReferralIncomes:", error);
    throw error;
  }
}

/**
 * 获取可用余额
 * @param {string} address 要查询的地址
 * @returns {Promise<string>}
 */
export async function availableBalanceOf(address) {
  try {
    const balance = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "availableBalanceOf",
      args: [address]
    });
    return convertFromWei(balance, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in availableBalanceOf:", error);
    throw error;
  }
}

/**
 * 获取快照时的余额
 * @param {string} account 账户地址
 * @param {number} snapshotId 快照ID
 * @returns {Promise<string>}
 */
export async function balanceOfAt(account, snapshotId) {
  try {
    const balance = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "balanceOfAt",
      args: [account, snapshotId]
    });
    return convertFromWei(balance, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in balanceOfAt:", error);
    throw error;
  }
}

/**
 * 查询账户余额
 * @param {string} account 要查询的账户地址
 * @returns {Promise<string>} 返回格式化后的余额
 */
export async function balanceOf(account) {
  try {
    const balance = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "balanceOf",
      args: [account]
    });
    return convertFromWei(balance, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("查询余额时发生错误:", error);
    throw error;
  }
}


/**
 * 获取快照时的总供应量
 * @param {number} snapshotId 快照ID
 * @returns {Promise<string>}
 */
export async function totalSupplyAt(snapshotId) {
  try {
    const supply = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalSupplyAt",
      args: [snapshotId]
    });
    return convertFromWei(supply, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in totalSupplyAt:", error);
    throw error;
  }
}

/**
 * 创建快照
 * @returns {Promise<number>}
 */
export async function snapshot() {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "snapshot",
      args: []
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in snapshot:", error);
    throw error;
  }
}

/**
 * 增加授权额度
 * @param {string} spender 被授权地址
 * @param {number|string} addedValue 增加的额度
 * @returns {Promise<any>}
 */
export async function increaseAllowance(spender, addedValue) {
  try {
    const amountWei = convertToWei(addedValue, DEFAULT_DECIMALS);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "increaseAllowance",
      args: [spender, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in increaseAllowance:", error);
    throw error;
  }
}

/**
 * 减少授权额度
 * @param {string} spender 被授权地址
 * @param {number|string} subtractedValue 减少的额度
 * @returns {Promise<any>}
 */
export async function decreaseAllowance(spender, subtractedValue) {
  try {
    const amountWei = convertToWei(subtractedValue, DEFAULT_DECIMALS);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "decreaseAllowance",
      args: [spender, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in decreaseAllowance:", error);
    throw error;
  }
}

/**
 * 更新USD地址
 * @param {string} newUsdAddress 新的USD地址
 * @returns {Promise<any>}
 */
export async function updateUsdAddress(newUsdAddress) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "updateUsdAddress",
      args: [newUsdAddress]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in updateUsdAddress:", error);
    throw error;
  }
}

/**
 * 升级账户
 * @param {string} account 要升级的账户
 * @param {number} roleId 角色ID
 * @returns {Promise<any>}
 */
export async function upgradeAccount(account, roleId) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "upgradeAccount",
      args: [account, roleId]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in upgradeAccount:", error);
    throw error;
  }
}

/**
 * 升级到Genesis地址
 * @param {string} address 要升级的地址
 * @param {string} inviterAddr 邀请人地址
 * @returns {Promise<any>}
 */
export async function upgradeToGenesisAddress(address, inviterAddr) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "upgradeToGenesisAddress",
      args: [address, inviterAddr]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in upgradeToGenesisAddress:", error);
    throw error;
  }
}

/**
 * 升级到Level 2
 * @returns {Promise<any>}
 */
export async function upgradeToLevel2(polValue) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "upgradeToLevel2",
      args: [],
      value: polValue
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in upgradeToLevel2:", error);
    throw error;
  }
}

/**
 * 升级到Level 3
 * @returns {Promise<any>}
 */
export async function upgradeToLevel3(polValue) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "upgradeToLevel3",
      args: [],
      value: polValue
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in upgradeToLevel3:", error);
    throw error;
  }
}

/**
 * 提取ETH
 * @returns {Promise<any>}
 */
export async function withdrawalForETH() {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "withdrawalForETH",
      args: []
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in withdrawalForETH:", error);
    throw error;
  }
}

/**
 * 提取代币
 * @param {string} tokenAddr 代币地址
 * @returns {Promise<any>}
 */
export async function withdrawalForTokens(tokenAddr) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "withdrawalForTokens",
      args: [tokenAddr]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in withdrawalForTokens:", error);
    throw error;
  }
}

/**
 * 捐赠
 * @param {string} referrer 推荐人地址
 * @param {number} miningCount 挖矿数量
 * @param {string|BigInt} polValue 发送的POL数量（wei）
 * @param {Object} [observer] Optional onSubmitted / onReplaced / onReceipt callbacks
 * @returns {Promise<any>}
 */
export async function donate(referrer, miningCount, polValue, observer) {
  // Observers report lifecycle only. Their failures must not change a sent transaction.
  const observe = (name, value) => {
    try { Promise.resolve(observer?.[name]?.(value)).catch(() => {}); } catch { /* observer only */ }
  };
  try {
    console.log('donate', referrer, miningCount, polValue);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "donate",
      args: [referrer, miningCount],
      value: polValue
    });
    observe('onSubmitted', tx);
    if (observer) {
      const receipt = await waitForTransactionReceipt(config, {
        hash: tx, chainId: 137,
        onReplaced: replacement => observe('onReplaced', replacement),
      });
      observe('onReceipt', receipt);
    } else {
      await monitorTransaction(tx);
    }
    return tx;
  } catch (error) {
    console.error("Error in donate:", error);
    throw error;
  }
}

/**
 * 捐赠获取代币
 * @param {string} referrer 推荐人地址
 * @param {number} count 数量
 * @returns {Promise<any>}
 */
export async function donateForToken(referrer, count) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "donateForToken",
      args: [referrer, count]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in donateForToken:", error);
    throw error;
  }
}

/**
 * 检查地址是否为矿工
 * @param {string} address 要检查的地址
 * @returns {Promise<boolean>}
 */
export async function checkIfAddressIsMine(address) {
  try {
    const result = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "checkIfAddressIsMine",
      args: [address]
    });
    return result;
  } catch (error) {
    console.error("Error in checkIfAddressIsMine:", error);
    throw error;
  }
}

/**
 * 检查地址是否为Pine
 * @param {string} address 要检查的地址
 * @returns {Promise<boolean>}
 */
export async function checkIfAddressIsPine(address) {
  try {
    const result = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "checkIfAddressIsPine",
      args: [address]
    });
    return result;
  } catch (error) {
    console.error("Error in checkIfAddressIsPine:", error);
    throw error;
  }
}

/**
 * 检查地址是否为董事
 * @param {string} address 要检查的地址
 * @returns {Promise<boolean>}
 */
export async function checkIfAddressIsDirector(address) {
  try {
    const result = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "checkIfAddressIsDirector",
      args: [address]
    });
    return result;
  } catch (error) {
    console.error("Error in checkIfAddressIsDirector:", error);
    throw error;
  }
}

/**
 * 获取地址状态
 * @param {string} address 要查询的地址
 * @returns {Promise<number>}
 */
export async function addressState(address) {
  try {
    const state = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "addressState",
      args: [address]
    });
    console.log('state',state);
    return Number(state);
  } catch (error) {
    console.error("Error in addressState:", error);
    throw error;
  }
}

/**
 * 获取NFT发送次数
 * @param {string} address 要查询的地址
 * @returns {Promise<number>}
 */
export async function nftSentCount(address) {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "nftSentCount",
      args: [address]
    });
    return Number(count);
  } catch (error) {
    console.error("Error in nftSentCount:", error);
    throw error;
  }
}

/**
 * 获取用户捐赠次数
 * @param {string} address 要查询的地址
 * @returns {Promise<number>}
 */
export async function userDonateCount(address) {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "userDonateCount",
      args: [address]
    });
    return Number(count);
  } catch (error) {
    console.error("Error in userDonateCount:", error);
    throw error;
  }
}

/**
 * 获取自推荐次数
 * @param {string} address 要查询的地址
 * @returns {Promise<number>}
 */
export async function selfReferralCount(address) {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "selfReferralCount",
      args: [address]
    });
    return Number(count);
  } catch (error) {
    console.error("Error in selfReferralCount:", error);
    throw error;
  }
}

/**
 * 获取自推荐捐赠次数
 * @param {string} address 要查询的地址
 * @returns {Promise<number>}
 */
export async function selfReferralDonateCount(address) {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "selfReferralDonateCount",
      args: [address]
    });
    return Number(count);
  } catch (error) {
    console.error("Error in selfReferralDonateCount:", error);
    throw error;
  }
}

/**
 * 获取总捐赠金额
 * @returns {Promise<string>}
 */
export async function totalDonate() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalDonate",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in totalDonate:", error);
    throw error;
  }
}

/**
 * 获取总推荐金额
 * @returns {Promise<string>}
 */
export async function totalReferralAmount() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalReferralAmount",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in totalReferralAmount:", error);
    throw error;
  }
}

/**
 * 获取总推荐次数
 * @returns {Promise<number>}
 */
export async function totalReferrals() {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalReferrals",
      args: []
    });
    return Number(count);
  } catch (error) {
    console.error("Error in totalReferrals:", error);
    throw error;
  }
}

/**
 * 获取总奖励金额
 * @returns {Promise<string>}
 */
export async function totalRewards() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalRewards",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in totalRewards:", error);
    throw error;
  }
}

/**
 * 获取已发送的总奖励金额
 * @returns {Promise<string>}
 */
export async function totalRewardsSent() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalRewardsSent",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in totalRewardsSent:", error);
    throw error;
  }
}

/**
 * 获取总销毁数量
 * @returns {Promise<string>}
 */
export async function totalBurned() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "totalBurned",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in totalBurned:", error);
    throw error;
  }
}

/**
 * 获取激活地址数量
 * @returns {Promise<number>}
 */
export async function activatedAddressCount() {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "activatedAddressCount",
      args: []
    });
    return Number(count);
  } catch (error) {
    console.error("Error in activatedAddressCount:", error);
    throw error;
  }
}

/**
 * 获取Genesis地址数量
 * @returns {Promise<number>}
 */
export async function genesisAddressCount() {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "genesisAddressCount",
      args: []
    });
    return Number(count);
  } catch (error) {
    console.error("Error in genesisAddressCount:", error);
    throw error;
  }
}

/**
 * 获取迁移数量
 * @returns {Promise<number>}
 */
export async function migrationCount() {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "migrationCount",
      args: []
    });
    return Number(count);
  } catch (error) {
    console.error("Error in migrationCount:", error);
    throw error;
  }
}

/**
 * 获取推荐激活总数
 * @returns {Promise<number>}
 */
export async function referralActivationsTotal() {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "referralActivationsTotal",
      args: []
    });
    return Number(count);
  } catch (error) {
    console.error("Error in referralActivationsTotal:", error);
    throw error;
  }
}

/**
 * 获取董事升级次数
 * @returns {Promise<number>}
 */
export async function directorUpgradeCount() {
  try {
    const count = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "directorUpgradeCount",
      args: []
    });
    return Number(count);
  } catch (error) {
    console.error("Error in directorUpgradeCount:", error);
    throw error;
  }
}

/**
 * 获取在线区块号
 * @returns {Promise<number>}
 */
export async function onlineBlockNumber() {
  try {
    const blockNumber = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "onlineBlockNumber",
      args: []
    });
    return Number(blockNumber);
  } catch (error) {
    console.error("Error in onlineBlockNumber:", error);
    throw error;
  }
}

/**
 * 获取待添加流动性池数量
 * @returns {Promise<string>}
 */
export async function pendingAddToLiquidityPool() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "pendingAddToLiquidityPool",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in pendingAddToLiquidityPool:", error);
    throw error;
  }
}

/**
 * 获取奖励兑换最小数量
 * @returns {Promise<string>}
 */
export async function rewardExchangeMinAmount() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "rewardExchangeMinAmount",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in rewardExchangeMinAmount:", error);
    throw error;
  }
}

/**
 * 获取升级费用（USDT）
 * @returns {Promise<string>}
 */
export async function upgradeFeeUSDT() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "upgradeFeeUSDT",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in upgradeFeeUSDT:", error);
    throw error;
  }
}

/**
 * 获取现货费用（USDT）
 * @returns {Promise<string>}
 */
export async function spotFeeUSDT() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "spotFeeUSDT",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in spotFeeUSDT:", error);
    throw error;
  }
}

/**
 * 获取默认角色ID
 * @returns {Promise<number>}
 */
export async function defaultRoleId() {
  try {
    const roleId = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "defaultRoleId",
      args: []
    });
    return Number(roleId);
  } catch (error) {
    console.error("Error in defaultRoleId:", error);
    throw error;
  }
}

/**
 * 获取MysteryKey NFT类型
 * @returns {Promise<number>}
 */
export async function mysteryKeyNftType() {
  try {
    const type = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "mysteryKeyNftType",
      args: []
    });
    return Number(type);
  } catch (error) {
    console.error("Error in mysteryKeyNftType:", error);
    throw error;
  }
}

/**
 * 获取最大激活限制
 * @returns {Promise<number>}
 */
export async function MAX_ACTIVATION_LIMIT() {
  try {
    const limit = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "MAX_ACTIVATION_LIMIT",
      args: []
    });
    return Number(limit);
  } catch (error) {
    console.error("Error in MAX_ACTIVATION_LIMIT:", error);
    throw error;
  }
}

/**
 * 获取Genesis分配数量
 * @returns {Promise<string>}
 */
export async function GENESIS_ALLOCATION_AMOUNT() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "GENESIS_ALLOCATION_AMOUNT",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in GENESIS_ALLOCATION_AMOUNT:", error);
    throw error;
  }
}

/**
 * 获取治理迁移数量
 * @returns {Promise<string>}
 */
export async function GOVERNANCE_MIGRATION_AMOUNT() {
  try {
    const amount = await readContract(config, {
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "GOVERNANCE_MIGRATION_AMOUNT",
      args: []
    });
    return convertFromWei(amount, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in GOVERNANCE_MIGRATION_AMOUNT:", error);
    throw error;
  }
}

/**
 * 创建Genesis地址
 * @param {string} _genesisAddress 要创建的Genesis地址
 * @returns {Promise<string>} Submitted transaction hash; caller observes receipt.
 */
export async function createGenesisAddress(_genesisAddress) {
  try {
    return await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "createGenesisAddress",
      args: [_genesisAddress]
    });
  } catch (error) {
    console.error("Error in createGenesisAddress:", error);
    throw error;
  }
}


/**
 * 調用 migrateOfficial 函數，將用戶遷移到官方推薦人下
 * @param {string} referrer 推薦人地址
 * @param {string} account 用戶地址
 * @param {string|number|BigInt} miningCount 礦機數量
 * @returns {Promise<void>}
 */
export async function migrateOfficial(referrer, account, miningCount) {
  try {
    await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.pctContractAddress,
      functionName: "migrateOfficial",
      args: [referrer, account, miningCount]
    });
  } catch (error) {
    console.error("Error in migrateOfficial:", error);
    throw error;
  }
}



/**
 * 通过Pair合约和主币价格动态计算PCT的美元价格
 * @returns {Promise<number>} PCT的美元价格
 */
export async function getPCTRealPrice() {
  try {
    // 获取储备量
    const { reserve0, reserve1 } = await getPairReserves();
    // 获取token0地址
    const token0 = await getPairToken0();
    // 判断token0是否为PCT
    const isToken0PCT = token0.toLowerCase() === addresses.pctContractAddress.toLowerCase();
    // 获取主币价格
    const maticPrice = await getPriceETH() || 0;

    // 计算
    let pctReserve, mainReserve;
    if (isToken0PCT) {
      pctReserve = new BigNumber(reserve0).div(1e18);
      mainReserve = new BigNumber(reserve1).div(1e18);
    } else {
      pctReserve = new BigNumber(reserve1).div(1e18);
      mainReserve = new BigNumber(reserve0).div(1e18);
    }
    if (pctReserve.isZero()) return 0;
    const pctPrice = mainReserve.times(maticPrice).div(pctReserve).toNumber();
    return pctPrice;
  } catch (e) {
    console.error('Error in getPCTRealPrice:', e);
    throw e;
  }
}

/**
 * 查詢最近N條松樹購買記錄（V2 地址過濾、分頁後選取最近記錄）
 * @param {string} userAddress 用戶地址
 * @param {number} count 條數，默認10
 * @returns {Promise<Array<{count:string,amount:string,txHash:string,blockNumber:number,time:string}>>}
 */
export async function fetchPineBuyRecords(userAddress, count = 10) {
  if (!userAddress) return [];
  const topic0 = new Interface(abi).getEvent('DonateToPineconeHistory').topicHash;
  const logs = await queryRecentContractEvents({
    contractAddress: addresses.pctContractAddress, topic0, address: userAddress,
    fromBlock: 0, toBlock: 'latest', chainId: 137,
  }, count);
  return logs.map(log => {
    const event = new Interface(abi).parseLog(log);
    const date = log.timeStamp ? new Date(Number(log.timeStamp) * 1000) : null;
    const pad = value => String(value).padStart(2, '0');
    return {
      count: event.args.count.toString(), amount: convertFromWei(event.args.amount, 18, 2),
      txHash: log.transactionHash, blockNumber: Number(log.blockNumber),
      time: date ? `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` : null,
    };
  });
}

/**
 * 获取转账税率（千分比）
 * @returns {Promise<number>}
 */
export async function getTransferTaxRatePerMille() {
  try {
    const rate = await readContract(config, {
      abi: rewardsAbi,
      address: addresses.rewardAddress,
      functionName: "transferTaxRatePerMille",
      args: []
    });
    return Number(rate);
  } catch (error) {
    console.error("Error in getTransferTaxRatePerMille:", error);
    throw error;
  }
}

/**
 * 獲取流動性池比例
 * @returns {Promise<number>}
 */
export async function getLiquidityPoolRate() {
  try {
    const rate = await readContract(config, {
      abi: rewardsAbi,
      address: addresses.rewardAddress,
      functionName: "liquidityPoolRate",
      args: []
    });
    return Number(rate);
  } catch (error) {
    console.error("Error in getLiquidityPoolRate:", error);
    throw error;
  }
}

/**
 * 獲取銷毀比例
 * @returns {Promise<number>}
 */
export async function getBurnRate() {
  try {
    const rate = await readContract(config, {
      abi: rewardsAbi,
      address: addresses.rewardAddress,
      functionName: "burnRate",
      args: []
    });
    return Number(rate);
  } catch (error) {
    console.error("Error in getBurnRate:", error);
    throw error;
  }
}

/**
 * 更新税率
 * @param {number} newRate 新的总税率（千分比）
 * @param {number} liquidityPoolRate 流动性池比例（百分比）
 * @param {number} burnRate 销毁比例（百分比）
 * @returns {Promise<any>}
 */
export async function updateTaxRates(newRate, liquidityPoolRate, burnRate) {
  try {
    console.log('updateTaxRates function called with:', { newRate, liquidityPoolRate, burnRate });
    console.log('Using rewardAddress:', addresses.rewardAddress);
    console.log('Function name:', 'updateTaxRates');
    console.log('Config object:', config);
    
    // 使用與項目其他地方相同的調用方式 - 直接返回交易哈希
    const tx = await writeContract(config, {
      chainId: 137,
      abi: rewardsAbi,
      address: addresses.rewardAddress,
      functionName: "updateTaxRates",
      args: [newRate, liquidityPoolRate, burnRate]
    });
    
    console.log('Transaction hash:', tx);
    
    if (tx) {
      await monitorTransaction(tx);
    }
    return tx;
  } catch (error) {
    console.error("Error in updateTaxRates - Full error:", error);
    console.error("Error details:", {
      message: error.message,
      cause: error.cause,
      stack: error.stack
    });
    throw error;
  }
}
